import { Config } from 'tailwindcss';
import plugin from 'tailwindcss/plugin';
import prose from '@tailwindcss/typography/src';
import ariaAttributes from 'tailwindcss-aria-attributes';
import twContainerQueries from '@tailwindcss/container-queries/dist';

export default {
  content: ['./index.html', './src/**/*.{js,ts,jsx,tsx}'],
  theme: {
    fontSize: {
      'body-sm': [
        '0.875rem',
        { lineHeight: '1.125rem', fontWeight: 400, letterSpacing: '0.25px' },
      ],
      'body-md': [
        '1rem',
        { lineHeight: '1.5rem', fontWeight: 400, letterSpacing: '0.5px' },
      ],
      'body-lg': [
        '1.25rem',
        { lineHeight: '1.75rem', fontWeight: 400, letterSpacing: '0.75px' },
      ],
      'label-sm': [
        '0.75rem',
        { lineHeight: '1rem', fontWeight: 700, letterSpacing: '0.5px' },
      ],
      'label-md': [
        '0.875rem',
        { lineHeight: '1.25rem', fontWeight: 600, letterSpacing: '0.15px' },
      ],
      'label-lg': [
        '1rem',
        { lineHeight: '1.5rem', fontWeight: 600, letterSpacing: '0.5px' },
      ],
      'title-sm': [
        '1rem',
        { lineHeight: '1.5rem', fontWeight: 500, letterSpacing: '0.15px' },
      ],
      'title-md': [
        '1.125rem',
        { lineHeight: '1.75rem', fontWeight: 700, letterSpacing: '0.15px' },
      ],
      'title-lg': [
        '1.5rem',
        { lineHeight: '2rem', fontWeight: 700, letterSpacing: '0px' },
      ],
      'headline-sm': [
        '1.75rem',
        { lineHeight: '2.25rem', fontWeight: 700, letterSpacing: '0px' },
      ],
      'headline-md': [
        '2rem',
        { lineHeight: '2.5rem', fontWeight: 700, letterSpacing: '0px' },
      ],
      'headline-lg': [
        '2.25rem',
        { lineHeight: '2.75rem', fontWeight: 700, letterSpacing: '0px' },
      ],
      inherit: 'inherit',
    },
    colors: {
      'theme-colors-nav': {
        DEFAULT: 'var(--theme-colors-nav, #34415A)',
        contrast: 'var(--theme-colors-nav-contrast, #FFFFFF)',
        dark: 'var(--theme-colors-nav-dark, #232C3D)',
        'dark-contrast': 'var(--theme-colors-nav-dark-contrast, #FFFFFF)',
        light: 'var(--theme-colors-nav-light, #757E8F)',
        'light-contrast': 'var(--theme-colors-nav-light-contrast, #FFFFFF)',
      },
      'theme-colors-primary': {
        light: 'var(--theme-colors-primary-light, #8EA385)',
        'light-contrast': 'var(--theme-colors-primary-light-contrast, #FFFFFF)',
        DEFAULT: 'var(--theme-colors-primary, #59784B)',
        contrast: 'var(--theme-colors-primary-contrast, #FFFFFF)',
        dark: 'var(--theme-colors-primary-dark, #3D5233)',
        'dark-contrast': 'var(--theme-colors-primary-dark-contrast, #FFFFFF)',
      },
      'theme-colors-secondary': {
        light: 'var(--theme-colors-secondary-light)',
        'light-contrast': 'var(--theme-colors-secondary-light-contrast)',
        DEFAULT: 'var(--theme-colors-secondary)',
        contrast: 'var(--theme-colors-secondary-contrast)',
        dark: 'var(--theme-colors-secondary-dark)',
        'dark-contrast': 'var(--theme-colors-secondary-dark-contrast)',
      },

      positive: {
        50: '#F4FDF3',
        500: '#008552',
        900: '#043D27',
      },
      negative: {
        50: '#FFF0F0',
        500: '#D42D2D',
        900: '#751313',
      },
      attention: {
        50: '#FEFCEB',
        500: '#BA5900',
        900: '#7A3C02',
      },
      prominence: {
        50: '#F4FAFF',
        500: '#0371DF',
        900: '#073C71',
      },
      importance: {
        50: '#ECE3F5',
        500: '#A560EF',
        900: '#421C6B',
      },
      neutral: {
        0: '#FFFFFF',
        50: '#F9F9F9',
        100: '#F1F1F1',
        200: '#E9E9E9',
        300: '#CDCDCD',
        400: '#A5A5A5',
        500: '#767676',
        550: '#5F5F5F',
        600: '#4A4A4A',
        700: '#2B2B2B',
        800: '#191919',
        900: '#111111',
      },
      white: '#FFFFFF',
      black: '#000000',
      inherit: 'inherit',
      current: 'currentColor',
      transparent: 'transparent',
    },

    extend: {
      placeholderColor: {
        neutral: {
          550: '#5F5F5F',
        },
      },
      borderWidth: {
        1: '1px',
        12: '12px',
      },
      fontFamily: {
        sans: 'var(--theme-font-family)',
      },
      outlineOffset: {
        10: '10px',
      },

      gridTemplateColumns: {
        fluid: 'repeat(auto-fit, 312px)',
      },
      keyframes: {
        hide: {
          from: { opacity: '1' },
          to: { opacity: '0' },
        },
        slideIn: {
          from: {
            transform: 'translateX(calc(100% + var(--viewport-padding)))',
          },
          to: { transform: 'translateX(0)' },
        },
        swipeOut: {
          from: { transform: 'translateX(var(--radix-toast-swipe-end-x))' },
          to: { transform: 'translateX(calc(100% + var(--viewport-padding)))' },
        },
        sheetOverlayShow: {
          from: { opacity: '0' },
          to: { opacity: '0.1' },
        },
        sheetContentSlideIn: {
          from: { transform: 'translateX(100%)' },
          to: { transform: 'translateX(0)' },
        },
        sheetContentSlideOut: {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(100%)' },
        },
        sheetContentSlideInRtl: {
          from: { transform: 'translateX(-100%)' },
          to: { transform: 'translateX(0)' },
        },
        sheetContentSlideOutRtl: {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-100%)' },
        },
      },
      animation: {
        hide: 'hide 150ms ease-in',
        slideIn: 'slideIn 150ms cubic-bezier(0.16, 1, 0.3, 1)',
        swipeOut: 'swipeOut 150ms ease-out',
        sheetOverlayShow: 'sheetOverlayShow 300ms ease',
        sheetContentSlideIn: 'sheetContentSlideIn 250ms ease-out',
        sheetContentSlideOut: 'sheetContentSlideOut 250ms ease-in',
        sheetContentSlideInRtl: 'sheetContentSlideInRtl 250ms ease-out',
        sheetContentSlideOutRtl: 'sheetContentSlideOutRtl 250ms ease-in',
      },
      containers: {
        '2xs': '16rem', // 256px
        xs: '20rem', // 320px
        sm: '24rem', // 384px
        md: '28rem', // 448px
        lg: '32rem', // 512px
        xl: '36rem', // 576px
        '2xl': '42rem', // 672px
        '3xl': '48rem', // 768px
        '4xl': '56rem', // 896px
        '5xl': '64rem', // 1024
        '6xl': '72rem', // 1152px
        '7xl': '80rem', // 1280px
      },
    },
    boxShadow: {
      50: '0px 2px 2px 0px rgba(0, 0, 0, 0.10)',
      100: '0px 4px 4px 0px rgba(0, 0, 0, 0.10)',
      'labeled-selection': '0px 3px 0px 0px var(--Neutral-100, #F1F1F1)',
    },
    container: {
      center: true,
    },
  },
  plugins: [
    prose,
    ariaAttributes,
    twContainerQueries,
    plugin(({ addBase, theme }) => {
      addBase({
        html: {
          color: theme('colors.neutral.900'),
        },
        'input, input[type="search"], button, a, div[role=menuitem]': {
          outlineOffset: '4px',
          outlineWidth: '2px',
        },
      });
    }),
  ],
} satisfies Config;
